import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, RotateCcw, Compass } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader, ProgressBar } from "@/components/common";
import { IdeaBadges } from "@/components/IdeaCard";
import { TYPE_OPTS, fitReasons, scoreIdea, shortRs, REVENUE_DISCLAIMER } from "@/lib/ideas";

const QUESTIONS = [
  ["budget", "What budget can you start with?", false, [[5000, "Under Rs 5,000"], [10000, "Up to Rs 10,000"], [25000, "Up to Rs 25,000"], [50000, "Up to Rs 50,000"], [null, "More than Rs 50,000"]]],
  ["time", "How much time do you have?", false, [["evenings", "A few evenings a week"], ["weekends", "Mainly weekends"], ["flexible", "Flexible, 5–10 hrs/week"], ["full_time", "I could go full-time"]]],
  ["types", "What do you prefer? (choose any)", true, TYPE_OPTS],
  ["skill", "What skill level do you want?", false, [["beginner", "Beginner — easy to start"], ["intermediate", "Some experience is fine"], ["any", "No preference"]]],
  ["fast_revenue", "How important is fast revenue?", false, [["very", "Very important"], ["somewhat", "Somewhat"], ["not", "Not a priority"]]],
  ["weekends", "Do you want something you can do on weekends?", false, [[true, "Yes"], [false, "Doesn't matter"]]],
];

function Quiz({ initial, onDone }) {
  const [step, setStep] = useState(0);
  const [a, setA] = useState(initial || { types: [] });
  const [key, title, multi, opts] = QUESTIONS[step];
  const pick = (v) => {
    if (multi) return setA({ ...a, types: a.types?.includes(v) ? a.types.filter((x) => x !== v) : [...(a.types || []), v] });
    const next = { ...a, [key]: v };
    setA(next);
    if (step < QUESTIONS.length - 1) setTimeout(() => setStep(step + 1), 150); else onDone(next);
  };
  const selected = (v) => (multi ? a.types?.includes(v) : a[key] === v && key in a);
  return (
    <div className="max-w-xl" data-testid="find-quiz">
      <div className="flex items-center gap-3 mb-6"><ProgressBar value={(100 * (step + 1)) / QUESTIONS.length} /><span className="text-xs font-bold text-[#8C9BA5] shrink-0">{step + 1} / {QUESTIONS.length}</span></div>
      <h2 className="text-2xl font-extrabold fade-up" key={key} data-testid="find-question">{title}</h2>
      <div className="mt-5 space-y-2.5">
        {opts.map(([v, l]) => (
          <button key={String(v)} onClick={() => pick(v)} data-testid={`find-option-${key}-${String(v)}`}
            className={`w-full min-h-[56px] rounded-2xl border-2 px-5 text-left font-semibold flex items-center justify-between transition-colors ${selected(v) ? "border-[#1DB99A] bg-[#E8F8F4]" : "border-[#EADEC9] bg-white hover:border-[#1DB99A]/50"}`}>
            {l}{selected(v) && <Check className="h-5 w-5 text-[#1DB99A]" />}
          </button>
        ))}
      </div>
      <div className="flex gap-3 mt-6">
        <button onClick={() => setStep(step - 1)} disabled={step === 0} className="sb-btn-ghost" data-testid="find-back-btn"><ArrowLeft className="h-4 w-4" />Back</button>
        {multi && <button onClick={() => setStep(step + 1)} className="sb-btn-navy flex-1" data-testid="find-next-btn">Next<ArrowRight className="h-4 w-4" /></button>}
      </div>
    </div>
  );
}

function Results({ ideas, prefs, onRetake }) {
  const list = useMemo(() => ideas.map((i) => ({ i, s: scoreIdea(i, prefs) })).filter((x) => x.s > 0)
    .sort((x, y) => y.s - x.s || (y.i.estimated_monthly_revenue_high || 0) - (x.i.estimated_monthly_revenue_high || 0)).slice(0, 9), [ideas, prefs]);
  return (
    <div data-testid="find-results">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <p className="text-[#5C6B73]">Based on your preferences, these ideas may fit you. Explore them and decide what feels right.</p>
        <button onClick={onRetake} className="sb-btn-ghost min-h-[40px] text-sm" data-testid="find-retake-btn"><RotateCcw className="h-4 w-4" />Change answers</button>
      </div>
      {!list.length && <div className="sb-card p-8 text-center text-sm text-[#5C6B73]">No close matches — try a higher budget or fewer preferences.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {list.map(({ i }) => (
          <Link key={i.number} to={`/ideas/${i.number}`} className="sb-card p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`find-result-${i.number}`}>
            <div className="text-[11px] font-semibold text-[#8C9BA5]">{i.category}</div>
            <h3 className="font-display font-bold text-lg mt-0.5">{i.name}</h3>
            <div className="grid grid-cols-2 gap-2 my-3 text-[11px]">
              <div className="rounded-lg bg-[#FBF8F2] px-2 py-1.5"><div className="text-[#8C9BA5]">Startup</div><b>{shortRs(i.startup_cost)}</b></div>
              <div className="rounded-lg bg-[#E8F8F4] px-2 py-1.5"><div className="text-[#179B80]">Monthly potential</div><b>{shortRs(i.revenue_monthly_text)}</b></div>
            </div>
            <ul className="space-y-1 flex-1">{fitReasons(i, prefs).slice(0, 4).map((r) => <li key={r} className="flex gap-1.5 text-xs"><Check className="h-3.5 w-3.5 text-[#1DB99A] mt-0.5 shrink-0" />{r}</li>)}</ul>
            <div className="mt-3"><IdeaBadges tags={i.tags} max={2} /></div>
          </Link>
        ))}
      </div>
      <p className="text-[11px] text-[#8C9BA5] mt-5">{REVENUE_DISCLAIMER}</p>
    </div>
  );
}

export default function FindMyBusiness() {
  const { user, updateProfile } = useAuth();
  const [ideas, setIdeas] = useState(null);
  const [quiz, setQuiz] = useState(!user.preferences);
  useEffect(() => { api.get("/ideas").then((r) => setIdeas(r.data.ideas)); }, []);
  const done = async (prefs) => { await updateProfile({ preferences: prefs }); setQuiz(false); };
  if (!ideas) return <Loader />;
  return (
    <div data-testid="find-my-business-page">
      <Link to="/ideas" className="inline-flex items-center gap-1 text-sm font-semibold text-[#5C6B73] mb-4"><ArrowLeft className="h-4 w-4" /> All ideas</Link>
      <PageHeader eyebrow={<span className="inline-flex items-center gap-1.5"><Compass className="h-3.5 w-3.5" />Find my business</span>} title={quiz ? "Let's find ideas that fit you" : "Ideas that may fit you"} />
      {quiz ? <Quiz initial={user.preferences} onDone={done} /> : <Results ideas={ideas} prefs={user.preferences} onRetake={() => setQuiz(true)} />}
    </div>
  );
}
